import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminShell } from '../components/admin/AdminShell';
import { adminApi } from '../services/adminApi';
import { RefreshCw } from 'lucide-react';
import {
  PageContainer,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  AlertMessage,
  LoadingSpinner,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  SecondaryButton,
  DangerButton,
} from '../styles/adminShared';

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: 15px;
  color: #1e293b;
  word-break: break-all;
`;

const MonoValue = styled.code`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 13px;
  background: #f1f5f9;
  padding: 8px 12px;
  border-radius: 6px;
  display: block;
  word-break: break-all;
  color: #334155;
`;

const CardIcon = styled.span`
  font-size: 24px;
`;

// Page-specific styled components for API key section
const ApiKeySection = styled.div`
  background: linear-gradient(135deg, #1a365d 0%, #2c5282 100%);
  border-radius: 12px;
  padding: 24px;
  color: white;
  margin-bottom: 24px;
`;

const ApiKeyTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ApiKeyDescription = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  opacity: 0.9;
`;

const ApiKeyBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.1);
  padding: 12px 16px;
  border-radius: 8px;
`;

const ApiKeyValue = styled.code`
  flex: 1;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 14px;
  word-break: break-all;
`;

const CopyButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:focus {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`;

const ViewButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
  
  &:focus {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`;

const ApiKeyButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const RegenerateButton = styled.button`
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.4);
  padding: 8px 16px;
  border-radius: 6px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;

  &:hover {
    background: rgba(239, 68, 68, 0.35);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NewApiKeyBox = styled.div`
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
`;

const NewApiKeyLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 8px;
`;

const NewApiKeyValue = styled.code`
  display: block;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 14px;
  background: white;
  padding: 12px;
  border-radius: 6px;
  word-break: break-all;
  color: #166534;
  border: 1px solid #bbf7d0;
`;

const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.$variant) {
      case 'success': return '#dcfce7';
      case 'warning': return '#fef3c7';
      default: return '#dbeafe';
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success': return '#166534';
      case 'warning': return '#92400e';
      default: return '#1e40af';
    }
  }};
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
`;

const WarningIcon = styled.span`
  font-size: 20px;
`;

const WarningText = styled.div`
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
`;

interface TenantInfo {
  tenantId: string;
  name?: string;
  slug?: string;
  description?: string;
  logo?: string;
  emailDomain?: string;
  regionCode?: string;
  uniqueGovtIdType?: string;
  uniqueGovtId?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface UserInfo {
  tenantId?: string;
  apiKey?: string;
  slug?: string;
}

export const TenantProfile: React.FC = () => {
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [newKeyCopied, setNewKeyCopied] = useState(false);

  useEffect(() => {
    // Get user info from localStorage (includes API key from login response)
    // No backend API call needed - API key is already stored during login
    const storedUser = localStorage.getItem('userInfo');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserInfo(user);
        loadTenantInfo(user.tenantId);
      } catch (e) {
        setError('Failed to load user information');
        setLoading(false);
      }
    } else {
      setError('User not logged in');
      setLoading(false);
    }
  }, []);

  const loadTenantInfo = async (tenantId: string) => {
    try {
      const tenant = await adminApi.getTenant(tenantId);
      setTenantInfo(tenant as TenantInfo);
    } catch (err) {
      // If API fails, show basic info from localStorage
      setTenantInfo({
        tenantId: tenantId,
        name: 'Your Organization',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyApiKey = async () => {
    const apiKey = userInfo?.apiKey || tenantInfo?.tenantId;
    if (apiKey) {
      try {
        await navigator.clipboard.writeText(apiKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy');
      }
    }
  };

  const getMaskedApiKey = () => {
    const apiKey = userInfo?.apiKey;
    if (!apiKey) return '••••••••••••••••••••••••••••••••';
    if (apiKey.length <= 8) return '••••••••••••';
    return apiKey.slice(0, 4) + '••••••••••••••••••••••••••••' + apiKey.slice(-4);
  };

  const handleRegenerateApiKey = async () => {
    setRegenerating(true);
    try {
      const result = await adminApi.regenerateApiKey();
      setNewApiKey(result.apiKey);
      // Update localStorage so the UI reflects the new key
      if (userInfo) {
        const updated = { ...userInfo, apiKey: result.apiKey };
        setUserInfo(updated);
        localStorage.setItem('userInfo', JSON.stringify(updated));
      }
    } catch (err) {
      setError('Failed to regenerate API key. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyNewApiKey = async () => {
    if (newApiKey) {
      try {
        await navigator.clipboard.writeText(newApiKey);
        setNewKeyCopied(true);
        setTimeout(() => setNewKeyCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy');
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <AdminShell title="Tenant Profile">
        <LoadingSpinner>Loading tenant information...</LoadingSpinner>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Tenant Profile">
      <PageContainer>
        {error && <AlertMessage $variant="error">{error}</AlertMessage>}

        <ApiKeySection>
          <ApiKeyTitle>
            🔑 API Key
          </ApiKeyTitle>
          <ApiKeyDescription>
            Use this API key to authenticate API requests from your applications. Keep it secure and never share it publicly.
          </ApiKeyDescription>
          <ApiKeyBox>
            <ApiKeyValue>
              {apiKeyVisible 
                ? (userInfo?.apiKey || 'No API key available') 
                : getMaskedApiKey()
              }
            </ApiKeyValue>
            <ApiKeyButtonGroup>
              <ViewButton 
                onClick={() => setApiKeyVisible(!apiKeyVisible)} 
                aria-label={apiKeyVisible ? "Hide API key" : "Show API key"}
              >
                {apiKeyVisible ? '🙈 Hide' : '👁️ View'}
              </ViewButton>
              <CopyButton onClick={handleCopyApiKey} aria-label="Copy API key">
                {copied ? (
                  <>✓ Copied</>
                ) : (
                  <>📋 Copy</>
                )}
              </CopyButton>
              <RegenerateButton
                onClick={() => setShowRegenerateModal(true)}
                aria-label="Regenerate API key"
              >
                <RefreshCw size={14} /> Regenerate
              </RegenerateButton>
            </ApiKeyButtonGroup>
          </ApiKeyBox>
          {newApiKey && (
            <NewApiKeyBox>
              <NewApiKeyLabel>New API Key (copy it now — it won't be shown again)</NewApiKeyLabel>
              <NewApiKeyValue>{newApiKey}</NewApiKeyValue>
              <CopyButton
                onClick={handleCopyNewApiKey}
                style={{ marginTop: 8, background: '#166534', color: 'white' }}
              >
                {newKeyCopied ? '✓ Copied' : '📋 Copy New Key'}
              </CopyButton>
            </NewApiKeyBox>
          )}
          <WarningBox>
            <WarningIcon>⚠️</WarningIcon>
            <WarningText>
              <strong>Security Notice:</strong> Your API key provides full access to your tenant's consent data.
              Never expose it in client-side code or public repositories. If compromised, regenerate it immediately.
            </WarningText>
          </WarningBox>
        </ApiKeySection>

        <Card>
          <CardHeader>
            <CardIcon>🏢</CardIcon>
            <CardTitle>Organization Details</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Tenant ID</InfoLabel>
                <MonoValue>{tenantInfo?.tenantId || 'N/A'}</MonoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Tenant Slug</InfoLabel>
                <MonoValue>{tenantInfo?.slug || userInfo?.slug || tenantInfo?.tenantId?.slice(0, 8) || 'N/A'}</MonoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Organization Name</InfoLabel>
                <InfoValue>{tenantInfo?.name || 'Not configured'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Description</InfoLabel>
                <InfoValue>{tenantInfo?.description || 'No description provided'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Email Domain</InfoLabel>
                <InfoValue>{tenantInfo?.emailDomain || 'Not configured'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Login URL</InfoLabel>
                <MonoValue style={{ fontSize: '12px' }}>
                  {`${window.location.origin}/t/${tenantInfo?.slug || userInfo?.slug || tenantInfo?.tenantId?.slice(0, 8) || 'your-slug'}/login`}
                </MonoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Region</InfoLabel>
                <InfoValue>
                  <Badge $variant="info">
                    {tenantInfo?.regionCode || 'IN'} - India
                  </Badge>
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  <Badge $variant="success">Active</Badge>
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardIcon>📋</CardIcon>
            <CardTitle>Registration Information</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Government ID Type</InfoLabel>
                <InfoValue>{tenantInfo?.uniqueGovtIdType || 'Not provided'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Government ID</InfoLabel>
                <InfoValue>{tenantInfo?.uniqueGovtId || 'Not provided'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Created At</InfoLabel>
                <InfoValue>{formatDate(tenantInfo?.createdAt)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Last Updated</InfoLabel>
                <InfoValue>{formatDate(tenantInfo?.updatedAt)}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardIcon>⚖️</CardIcon>
            <CardTitle>DPDP Compliance</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Compliance Status</InfoLabel>
                <InfoValue>
                  <Badge $variant="success">✓ DPDP Act 2023 Compliant</Badge>
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Data Residency</InfoLabel>
                <InfoValue>India (as per DPDP Act requirements)</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Consent Audit Trail</InfoLabel>
                <InfoValue>
                  <Badge $variant="success">✓ Enabled</Badge>
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Data Principal Rights</InfoLabel>
                <InfoValue>
                  <Badge $variant="success">✓ Supported</Badge>
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </CardBody>
        </Card>
        {showRegenerateModal && (
          <ModalOverlay $show={true} onClick={() => !regenerating && setShowRegenerateModal(false)}>
            <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ maxWidth: 480 }}>
              <ModalHeader>
                <ModalTitle>Regenerate API Key</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p style={{ margin: '0 0 12px', color: '#475569' }}>
                  This will <strong>permanently invalidate</strong> your current API key. Any applications using the old key will stop working immediately.
                </p>
                <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>
                  This action cannot be undone.
                </p>
              </ModalBody>
              <ModalFooter>
                <SecondaryButton
                  onClick={() => setShowRegenerateModal(false)}
                  disabled={regenerating}
                >
                  Cancel
                </SecondaryButton>
                <DangerButton
                  onClick={async () => {
                    await handleRegenerateApiKey();
                    setShowRegenerateModal(false);
                  }}
                  disabled={regenerating}
                >
                  {regenerating ? 'Regenerating...' : 'Regenerate API Key'}
                </DangerButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageContainer>
    </AdminShell>
  );
};

export default TenantProfile;
